import type { Getter, Setter } from "jotai";
import { atomEffect } from "jotai-effect";

import { $queryState, $queryStateKeys, removeQueryStateKeyFromDocuments } from "./query-state.atom";

// =============================================================================
// Query Cleanup Configuration
// =============================================================================

const CLEANUP_INTERVAL_MS = 30_000; // 30 seconds
const STALE_THRESHOLD_MS = 5 * 60_000; // 5 minutes

// =============================================================================
// Query Cleanup Effect
// =============================================================================

/**
 * Periodic cleanup effect that removes stale query state entries.
 *
 * Runs every 30 seconds and removes entries where lastAccessedAt > 5 minutes ago.
 * Uses $queryStateKeys to iterate over active keys.
 */
export function cleanupStaleQueryKeys(get: Getter, set: Setter, now = Date.now()): string[] {
	const keys = get($queryStateKeys);
	const keysToRemove: string[] = [];

	for (const key of keys) {
		const state = get($queryState(key));
		if (now - state.lastAccessedAt > STALE_THRESHOLD_MS) {
			keysToRemove.push(key);
		}
	}

	if (keysToRemove.length === 0) {
		return [];
	}

	set($queryStateKeys, (prev) => {
		const next = new Set(prev);
		for (const key of keysToRemove) {
			next.delete(key);
		}
		return next;
	});

	for (const key of keysToRemove) {
		removeQueryStateKeyFromDocuments(get, set, key);
	}

	return keysToRemove;
}

export const $queryCleanup = atomEffect((get, set) => {
	const cleanup = () => {
		cleanupStaleQueryKeys(get, set);
	};

	// Run cleanup on interval
	const intervalId = setInterval(cleanup, CLEANUP_INTERVAL_MS);

	// Cleanup on unmount
	return () => {
		clearInterval(intervalId);
	};
});
$queryCleanup.debugLabel = "queryCleanup";
